<template>
  <section class="page-card">
    <h1 class="page-title">{{ $t('pages.dashboard') }}</h1>

    <h2 class="section-title">{{ $t('dashboard.basicInfo') }}</h2>
    <div class="info-grid">
      <div><span>{{ $t('common.username') }}</span><strong>{{ profile.username || '-' }}</strong></div>
      <div><span>{{ $t('dashboard.name') }}</span><strong>{{ fullName || '-' }}</strong></div>
      <div><span>{{ $t('common.phone') }}</span><strong>{{ profile.phone || '-' }}</strong></div>
      <div><span>{{ $t('common.email') }}</span><strong>{{ profile.email || '-' }}</strong></div>
      <div><span>{{ $t('common.areaCode') }}</span><strong>{{ profile.area_code || '-' }}</strong></div>
      <div><span>{{ $t('common.currency') }}</span><strong>{{ wallet.currency_code || profile.currency_code || '-' }}</strong></div>
      <div><span>{{ $t('common.packageName') }}</span><strong>{{ profile.pkg || '-' }}</strong></div>
      <div><span>{{ $t('common.channel') }}</span><strong>{{ profile.chn || '-' }}</strong></div>
    </div>

    <div class="wallet-grid">
      <div class="wallet-panel">
        <h2>{{ $t('wallet.topupWallet') }}</h2>
        <div class="wallet-metrics">
          <div><span>{{ $t('wallet.topupBalance') }}</span><strong>{{ wallet.topup_balance || '0' }} {{ walletCurrency }}</strong></div>
          <div><span>{{ $t('wallet.totalRechargeAmount') }}</span><strong>{{ wallet.total_recharge_amount || '0' }} {{ walletCurrency }}</strong></div>
          <div><span>{{ $t('wallet.totalTopupAmount') }}</span><strong>{{ wallet.total_topup_amount || '0' }} {{ walletCurrency }}</strong></div>
        </div>
      </div>
      <div class="wallet-panel earnings">
        <h2>{{ $t('wallet.earningsWallet') }}</h2>
        <div class="wallet-metrics">
          <div><span>{{ $t('wallet.earningsBalance') }}</span><strong>{{ wallet.earnings_balance || '0' }} {{ walletCurrency }}</strong></div>
          <div><span>{{ $t('wallet.totalEarnings') }}</span><strong>{{ wallet.total_earnings || '0' }} {{ walletCurrency }}</strong></div>
          <div><span>{{ $t('wallet.totalWithdrawAmount') }}</span><strong>{{ wallet.total_withdraw_amount || '0' }} {{ walletCurrency }}</strong></div>
        </div>
      </div>
    </div>

    <h2 class="section-title">{{ $t('dashboard.acquisitionStats') }}</h2>
    <div class="filter-bar">
      <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd" range-separator="-" :start-placeholder="$t('common.start')" :end-placeholder="$t('common.end')" class="filter-item-wide" size="small"></el-date-picker>
      <el-input v-model="search.area_code" :placeholder="$t('common.areaCode')" class="filter-item" size="small"></el-input>
      <el-button type="primary" size="small" @click="loadData">{{ $t('actions.search') }}</el-button>
    </div>
    <div class="summary-grid">
      <div class="metric-card"><div class="metric-label">{{ $t('pages.newUsers') }}</div><div class="metric-value">{{ totals.new_user_count }}</div></div>
      <div class="metric-card">
        <div class="metric-label">{{ $t('common.rechargeAmount') }}</div>
        <div v-if="totalsByCurrency.length" class="currency-total-list">
          <div v-for="item in totalsByCurrency" :key="`recharge:${item.currency_code}`" class="currency-total-row">
            <strong>{{ item.recharge_amount }}</strong><span>{{ item.currency_code }}</span>
          </div>
        </div>
        <div v-else class="metric-value">0</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">{{ $t('common.withdrawAmount') }}</div>
        <div v-if="totalsByCurrency.length" class="currency-total-list">
          <div v-for="item in totalsByCurrency" :key="`withdraw:${item.currency_code}`" class="currency-total-row">
            <strong>{{ item.withdraw_amount }}</strong><span>{{ item.currency_code }}</span>
          </div>
        </div>
        <div v-else class="metric-value">0</div>
      </div>
      <div class="metric-card"><div class="metric-label">{{ $t('common.firstRechargeUsers') }}</div><div class="metric-value">{{ totals.first_recharge_user_count }}</div></div>
    </div>
    <div class="table-scroll">
      <el-table :data="tableData" border stripe>
        <el-table-column prop="date" :label="$t('common.date')" min-width="100"></el-table-column>
        <el-table-column prop="pkg" :label="$t('common.packageName')" min-width="150"></el-table-column>
        <el-table-column prop="area_code" :label="$t('common.areaCode')" min-width="80"></el-table-column>
        <el-table-column prop="currency_code" :label="$t('common.currency')" min-width="90"></el-table-column>
        <el-table-column prop="new_user_count" :label="$t('pages.newUsers')" min-width="90"></el-table-column>
        <el-table-column prop="new_register_user_count" :label="$t('pages.newRegisteredAccounts')" min-width="110"></el-table-column>
        <el-table-column prop="active_count" :label="$t('pages.activeUsers')" min-width="100"></el-table-column>
        <el-table-column :label="$t('common.rechargeAmount')" min-width="140">
          <template slot-scope="scope">{{ scope.row.recharge_amount }} {{ scope.row.currency_code }}</template>
        </el-table-column>
        <el-table-column prop="recharge_user" :label="$t('common.rechargeUsers')" min-width="110"></el-table-column>
        <el-table-column :label="$t('pages.newPaidAmount')" min-width="150">
          <template slot-scope="scope">{{ scope.row.new_recharge_amount }} {{ scope.row.currency_code }}</template>
        </el-table-column>
        <el-table-column prop="new_recharge_user" :label="$t('pages.newPaidUsers')" min-width="120"></el-table-column>
        <el-table-column :label="$t('common.withdrawAmount')" min-width="140">
          <template slot-scope="scope">{{ scope.row.withdraw_amount }} {{ scope.row.currency_code }}</template>
        </el-table-column>
        <el-table-column prop="withdraw_user" :label="$t('common.withdrawUsers')" min-width="120"></el-table-column>
        <el-table-column prop="first_recharge_user_count" :label="$t('common.firstRechargeUsers')" min-width="130"></el-table-column>
        <el-table-column prop="day_1_stay_rate" :label="$t('pages.day1Retention')" min-width="100"></el-table-column>
        <el-table-column prop="day_3_stay_rate" :label="$t('pages.day3Retention')" min-width="100"></el-table-column>
        <el-table-column prop="day_7_stay_rate" :label="$t('pages.day7Retention')" min-width="100"></el-table-column>
      </el-table>
    </div>
    <el-pagination :total="count" :current-page.sync="search.page" :page-size="search.size" layout="total, prev, pager, next" @current-change="loadData"></el-pagination>
  </section>
</template>

<script>
import { defaultDateRange, isDateRangeOverDays } from '../utils/date';

export default {
  name: 'Dashboard',
  data() {
    return {
      dateRange: defaultDateRange(),
      search: { page: 1, size: 20, area_code: '' },
      tableData: [],
      totalsByCurrency: [],
      count: 0,
      profile: {},
      wallet: {
        topup_balance: '0',
        earnings_balance: '0',
        total_recharge_amount: '0',
        total_topup_amount: '0',
        total_earnings: '0',
        total_withdraw_amount: '0',
        currency_code: ''
      }
    };
  },
  computed: {
    fullName() {
      return [this.profile.first_name, this.profile.last_name].filter(Boolean).join(' ');
    },
    walletCurrency() {
      return this.wallet.currency_code || this.profile.currency_code || '';
    },
    totals() {
      const total = { new_user_count: 0, first_recharge_user_count: 0 };
      this.tableData.forEach(item => {
        total.new_user_count += Number(item.new_user_count || 0);
        total.first_recharge_user_count += Number(item.first_recharge_user_count || 0);
      });
      return total;
    }
  },
  created() {
    this.loadData();
    this.loadOverview();
  },
  methods: {
    validateDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) return true;
      if (!isDateRangeOverDays(this.dateRange[0], this.dateRange[1], 31)) return true;
      this.$message.error(this.$t('common.dateRangeTooLong'));
      return false;
    },
    async loadData() {
      if (!this.validateDateRange()) return;
      const params = Object.assign({}, this.search, { date__gte: this.dateRange && this.dateRange[0], date__lte: this.dateRange && this.dateRange[1] });
      const resp = await this.$axios.get('/admin/exagent/console/stats/overview/', { params: params });
      if (resp.data.status !== 0) return;
      const data = resp.data.data || {};
      this.tableData = data.list || [];
      this.totalsByCurrency = data.totals_by_currency || [];
      this.count = data.count || 0;
    },
    async loadOverview() {
      const responses = await Promise.all([
        this.$axios.get('/admin/exagent/console/profile/'),
        this.$axios.get('/admin/exagent/console/wallet/')
      ]);
      if (responses[0].data.status === 0) this.profile = responses[0].data.data || {};
      if (responses[1].data.status === 0) this.wallet = Object.assign({}, this.wallet, responses[1].data.data || {});
    }
  }
};
</script>

<style scoped>
.section-title { margin: 22px 0 12px; font-size: 17px; }
.info-grid, .wallet-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.info-grid > div { padding: 12px; border: 1px solid #e6e8ef; border-radius: 6px; }
.info-grid span, .wallet-metrics span { display: block; margin-bottom: 5px; color: #778094; font-size: 12px; }
.wallet-panel { padding: 18px; border-radius: 8px; background: #f1f7ff; border: 1px solid #d9e9ff; }
.wallet-panel.earnings { background: #f3faf5; border-color: #d9efdf; }
.wallet-panel h2 { margin: 0 0 14px; font-size: 16px; }
.wallet-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.wallet-metrics strong { font-size: 18px; overflow-wrap: anywhere; }
.currency-total-list { display: grid; gap: 8px; margin-top: 8px; }
.currency-total-row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.currency-total-row strong { font-size: 20px; overflow-wrap: anywhere; }
.currency-total-row span { color: #778094; font-size: 12px; font-weight: 600; }
@media (max-width: 900px) { .info-grid, .wallet-grid { grid-template-columns: 1fr; } .wallet-metrics { grid-template-columns: 1fr; } }
</style>



// WEBPACK FOOTER //
// src/views/Dashboard.vue